import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TablePagination from '@material-ui/core/TablePagination';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { CSVLink } from 'react-csv';
import { FaFileExport } from 'react-icons/fa';
import classNames from 'classnames';

import { getAllCouponsByAdmin } from './queries';
import FilterFromToV2 from '../../../components/Filter/FilterFromToV2';
import styles from './styles';
import { colors } from '../../../constants';

const CustomTableCell = withStyles(theme => ({
  head: {
    backgroundColor: colors.parimary,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

class TableCoupon extends Component {
  state = {
    consumer: '',
    merchant: '',
    fromDateTriggered: '',
    toDateTriggered: '',
    fromDateRedeemed: '',
    toDateRedeemed: '',
    fromDateExpired: '',
    toDateExpired: '',
    page: 1,
    limit: 15,
    exportData: [],
    exporting: false,
  };

  componentDidUpdate(prevProps, prevState) {
    const filterKeys = [
      'page', 'limit', 'consumer', 'merchant',
      'fromDateTriggered', 'toDateTriggered', 'fromDateRedeemed', 'toDateRedeemed',
      'fromDateExpired', 'toDateExpired',
    ];
    const changed = filterKeys.some((key) => prevState[key] !== this.state[key]);
    if (changed) {
      this.refetchData();
    }
  }

  buildFiltersInput = () => {
    const {
      consumer, merchant, fromDateTriggered, toDateTriggered, fromDateRedeemed, toDateRedeemed,
      fromDateExpired, toDateExpired,
    } = this.state;
    return {
      consumer: consumer || null,
      merchant: merchant || null,
      fromDateTriggered: fromDateTriggered || null,
      toDateTriggered: toDateTriggered || null,
      fromDateRedeemed: fromDateRedeemed || null,
      toDateRedeemed: toDateRedeemed || null,
      fromDateExpired: fromDateExpired || null,
      toDateExpired: toDateExpired || null,
    };
  };

  refetchData = () => {
    const { refetch } = this.props;
    if (!refetch) return;
    const { page, limit } = this.state;
    refetch({
      input: {
        ...this.buildFiltersInput(),
        page: page || 1,
        limit: limit || 15,
      },
    });
  };

  handleFilterChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value, page: 1 });
  };

  handleChangePage = (event, page) => {
    this.setState({ page: page + 1 });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ limit: event.target.value, page: 1 });
  };

  formatDate = (value) => {
    if (!value) return '';
    const timestamp = Number(value);
    const date = !isNaN(timestamp) && timestamp > 0
      ? moment(timestamp)
      : moment(value);
    return date.isValid() ? date.format('MM/DD/YYYY HH:mm') : '';
  };

  formatRow = (coupon) => ({
    Consumer: coupon.consumer && coupon.consumer.username ? coupon.consumer.username : '',
    Merchant: coupon.merchant && coupon.merchant.username ? coupon.merchant.username : '',
    Value: coupon.value != null ? coupon.value : '',
    'Accumulated DealCoins': coupon.accumulatedDealCoins != null ? coupon.accumulatedDealCoins : '',
    Status: coupon.status || '',
    'Date triggered': this.formatDate(coupon.dateTriggered),
    'Date Redeemed': this.formatDate(coupon.dateRedeemed),
  });

  prepareDataForCSV = (coupons = []) => coupons.map((coupon) => this.formatRow(coupon));

  onExportToCSV = () => {
    const { client } = this.props;
    const { limit } = this.state;
    if (!client) return;

    this.setState({ exporting: true }, async () => {
      try {
        const baseInput = {
          ...this.buildFiltersInput(),
          page: 1,
          limit: limit || 15,
        };

        const firstResult = await client.query({
          query: getAllCouponsByAdmin,
          variables: { input: baseInput },
          fetchPolicy: 'no-cache',
        });
        const firstPayload = firstResult && firstResult.data
          ? firstResult.data.getAllCouponsByAdmin
          : null;

        if (!firstPayload || !firstPayload.count_rows) {
          this.setState({ exporting: false, exportData: [] });
          return;
        }

        let allCoupons = (firstPayload.coupons || []).slice();
        const totalPage = firstPayload.total_page || 1;

        for (let currentPage = 2; currentPage <= totalPage; currentPage += 1) {
          const pageResult = await client.query({
            query: getAllCouponsByAdmin,
            variables: { input: { ...baseInput, page: currentPage } },
            fetchPolicy: 'no-cache',
          });
          const pagePayload = pageResult && pageResult.data
            ? pageResult.data.getAllCouponsByAdmin
            : null;
          if (pagePayload && pagePayload.coupons && pagePayload.coupons.length) {
            allCoupons = allCoupons.concat(pagePayload.coupons);
          }
        }

        this.setState({
          exportData: this.prepareDataForCSV(allCoupons),
          exporting: false,
        }, () => {
          if (this.csvLinkRef) {
            this.csvLinkRef.link.click();
          }
        });
      } catch (error) {
        this.setState({ exporting: false });
      }
    });
  };

  renderFilter = () => {
    const {
      consumer, merchant, fromDateTriggered, toDateTriggered, fromDateRedeemed, toDateRedeemed,
      fromDateExpired, toDateExpired,
    } = this.state;
    const { classes } = this.props;

    return (
      <div>
        <div className={classes.filterRow}>
          <FilterFromToV2
            label="Consumer"
            fromField={
              <TextField name="consumer" value={consumer} onChange={this.handleFilterChange} placeholder="Consumer" />
            }
            resetFn={() => this.setState({ consumer: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Merchant"
            fromField={
              <TextField name="merchant" value={merchant} onChange={this.handleFilterChange} placeholder="Merchant" />
            }
            resetFn={() => this.setState({ merchant: '', page: 1 })}
            smallButton
          />
        </div>
        <div className={classes.filterRow}>
          <FilterFromToV2
            label="From Date Triggered"
            fromField={
              <TextField
                name="fromDateTriggered"
                type="date"
                value={fromDateTriggered}
                onChange={this.handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            }
            resetFn={() => this.setState({ fromDateTriggered: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="To Date Triggered"
            fromField={
              <TextField
                name="toDateTriggered"
                type="date"
                value={toDateTriggered}
                onChange={this.handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            }
            resetFn={() => this.setState({ toDateTriggered: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="From Date Redeemed"
            fromField={
              <TextField
                name="fromDateRedeemed"
                type="date"
                value={fromDateRedeemed}
                onChange={this.handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            }
            resetFn={() => this.setState({ fromDateRedeemed: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="To Date Redeemed"
            fromField={
              <TextField
                name="toDateRedeemed"
                type="date"
                value={toDateRedeemed}
                onChange={this.handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            }
            resetFn={() => this.setState({ toDateRedeemed: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Expired"
            fromField={
              <TextField
                name="fromDateExpired"
                type="date"
                value={fromDateExpired}
                onChange={this.handleFilterChange}
                placeholder="From"
                InputLabelProps={{ shrink: true }}
              />
            }
            toField={
              <TextField
                name="toDateExpired"
                type="date"
                value={toDateExpired}
                onChange={this.handleFilterChange}
                placeholder="To"
                InputLabelProps={{ shrink: true }}
              />
            }
            resetFn={() => this.setState({ fromDateExpired: '', toDateExpired: '', page: 1 })}
            smallButton
          />
        </div>
      </div>
    );
  };

  renderToolBar = () => {
    const { classes, couponsData } = this.props;
    const { exportData, exporting } = this.state;
    const countRows = (couponsData && couponsData.count_rows) || 0;

    return (
      <div className={classes.searchWrapper}>
        <Button
          variant="contained"
          color="primary"
          className={classNames(classes.buttonActive, classes.exportButton)}
          onClick={this.onExportToCSV}
          disabled={exporting}
        >
          <Typography component="p" className={classes.text}>
            {exporting ? 'Exporting...' : 'Export Filtered with Value to CSV'}
          </Typography>
          <FaFileExport color="#ffffff" size={16} />
        </Button>
        <CSVLink
          data={exportData}
          filename={`coupons-${moment().format('YYYY-MM-DD-HHmmss')}.csv`}
          style={{ display: 'none' }}
          ref={(r) => { this.csvLinkRef = r; }}
        >
          Export
        </CSVLink>
        <Typography component="p" style={{ fontSize: '14px' }}>
          Rows: {countRows}
        </Typography>
      </div>
    );
  };

  render() {
    const { classes, couponsData, loading } = this.props;
    const { page, limit } = this.state;

    if (loading && !couponsData) return null;

    const coupons = (couponsData && couponsData.coupons) || [];
    const countRows = (couponsData && couponsData.count_rows) || 0;

    return (
      <div>
        {this.renderFilter()}
        {this.renderToolBar()}
        <Paper className={classes.root}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <CustomTableCell>Consumer</CustomTableCell>
                <CustomTableCell>Merchant</CustomTableCell>
                <CustomTableCell align="right">Value</CustomTableCell>
                <CustomTableCell align="right">Accumulated DealCoins</CustomTableCell>
                <CustomTableCell>Status</CustomTableCell>
                <CustomTableCell align="right">Date triggered</CustomTableCell>
                <CustomTableCell align="right">Date Redeemed</CustomTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((coupon) => {
                const row = this.formatRow(coupon);
                return (
                  <TableRow className={classes.row} key={`${coupon.recordType}-${coupon.id}`}>
                    <CustomTableCell component="th" scope="row">{row.Consumer}</CustomTableCell>
                    <CustomTableCell>{row.Merchant}</CustomTableCell>
                    <CustomTableCell align="right">{row.Value}</CustomTableCell>
                    <CustomTableCell align="right">{row['Accumulated DealCoins']}</CustomTableCell>
                    <CustomTableCell>{row.Status}</CustomTableCell>
                    <CustomTableCell align="right">{row['Date triggered']}</CustomTableCell>
                    <CustomTableCell align="right">{row['Date Redeemed']}</CustomTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={countRows}
            rowsPerPage={limit}
            page={page - 1}
            backIconButtonProps={{ 'aria-label': 'Previous Page' }}
            nextIconButtonProps={{ 'aria-label': 'Next Page' }}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
          />
        </Paper>
      </div>
    );
  }
}

TableCoupon.defaultProps = {
  classes: {},
  couponsData: null,
  loading: false,
  refetch: () => {},
  client: null,
};

TableCoupon.propTypes = {
  classes: PropTypes.object,
  couponsData: PropTypes.object,
  loading: PropTypes.bool,
  refetch: PropTypes.func,
  client: PropTypes.object,
};

const AllCouponsByAdmin = graphql(getAllCouponsByAdmin, {
  options: () => ({
    variables: {
      input: {
        page: 1,
        limit: 15,
        consumer: null,
        merchant: null,
        fromDateTriggered: null,
        toDateTriggered: null,
        fromDateRedeemed: null,
        toDateRedeemed: null,
        fromDateExpired: null,
        toDateExpired: null,
      },
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => ({
    loading: data.loading,
    couponsData: data.getAllCouponsByAdmin,
    refetch: data.refetch,
  }),
});

export default withStyles(styles)(compose(AllCouponsByAdmin, withApollo)(TableCoupon));
